import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useAuth0 } from '@auth0/auth0-react'
import {
  signOut,
  createGame,
  joinGame,
  clearLastCreatedGame,
  resolvePostJoinRedirectPath
} from 'store/auth'
import ROUTES from 'constants/routes'
import { useHistory } from 'react-router-dom'
import { USER_TYPES } from 'constants/users'
import { toast } from 'react-toastify'
import { getEmailFromAuth0User } from 'utils/auth0'
import clockIcon from 'assets/icons/clock-small.png'
import communityIcon from 'assets/icons/community.webp'
import shoppingBagIcon from 'assets/icons/shopping-bag.webp'
import greenVehicleIcon from 'assets/icons/green-vehicle.webp'
import starIcon from 'assets/icons/star.webp'

const OVERVIEW_STATS = [
  { key: 'years', value: 7, icon: clockIcon },
  { key: 'teams', value: 5, icon: communityIcon },
  { key: 'markets', value: 5, icon: shoppingBagIcon },
  { key: 'products', value: 4, icon: greenVehicleIcon },
  { key: 'winner', value: 1, icon: starIcon }
]

export default function GameSelection() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { logout, user } = useAuth0()

  const { userType, lastCreatedGame, loggedIn } = useSelector((store) => store.auth)
  const email = getEmailFromAuth0User(user)

  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [joinCode, setJoinCode] = useState('')
  const [creating, setCreating] = useState(false)

  // Clear any previous game when entering Game Selection so we don't use stale data after create/join
  useEffect(() => {
    dispatch(clearLastCreatedGame())
  }, [dispatch])

  // Redirect based on game state after creation or join
  // Wait for both lastCreatedGame and loggedIn (API sign-in complete)
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (
        !lastCreatedGame ||
        !loggedIn ||
        !(userType === USER_TYPES.player || userType === USER_TYPES.facilitator)
      ) {
        return
      }
      const path = await dispatch(resolvePostJoinRedirectPath(lastCreatedGame, userType, email))
      if (!cancelled && path) {
        history.replace(path)
      }
    }
    run().catch(() => {})
    return () => {
      cancelled = true
    }
  }, [lastCreatedGame, userType, loggedIn, history, email, dispatch])

  const handleLogout = () => {
    dispatch(signOut())
    logout({ logoutParams: { returnTo: window.location.origin + ROUTES.APP_URL_LOGIN } })
  }

  const handleCreateGame = async (e) => {
    e.preventDefault()
    if (!createName.trim()) {
      toast.error(t('game_selection.create_name_required'))
      return
    }
    if (!email) {
      toast.error(t('game_selection.email_required'))
      return
    }
    setCreating(true)
    try {
      const gameData = await dispatch(
        createGame(createName.trim(), createDescription.trim(), email)
      )
      if (gameData) {
        const path = await dispatch(resolvePostJoinRedirectPath(gameData, userType, email))
        if (path) history.replace(path)
      }
    } finally {
      setCreating(false)
    }
  }

  const handleJoinGame = async (e) => {
    e.preventDefault()
    if (!joinCode.trim()) {
      toast.error(t('game_selection.join_code_required'))
      return
    }
    const normalizedCode = joinCode.trim().toUpperCase()
    if (!email) {
      toast.error(t('game_selection.email_required_join'))
      return
    }
    const gameData = await dispatch(joinGame(normalizedCode, email))
    if (gameData) {
      const path = await dispatch(resolvePostJoinRedirectPath(gameData, userType, email))
      if (path) history.replace(path)
    }
  }

  return (
    <div className="game-selection fade-in">
      <div
        className="game-selection__corner-dot game-selection__corner-dot--top-left"
        aria-hidden="true"
      />
      <div
        className="game-selection__corner-dot game-selection__corner-dot--top-right"
        aria-hidden="true"
      />
      <div
        className="game-selection__corner-dot game-selection__corner-dot--bottom-left"
        aria-hidden="true"
      />
      <div
        className="game-selection__corner-dot game-selection__corner-dot--bottom-right"
        aria-hidden="true"
      />

      <div className="game-selection__shell">
        <div className="game-selection__layout">
          <div className="game-selection__left-column">
            <section className="game-selection__card game-selection__card--create">
              <div className="game-selection__card-content">
                <form onSubmit={handleCreateGame} className="game-selection__form">
                  <h2 className="game-selection__card-title game-selection__card-title--create">
                    {t('game_selection.create_title')}
                  </h2>
                  <div className="game-selection__form-group">
                    <label className="game-selection__field" htmlFor="game-selection-create-name">
                      <span className="game-selection__field-label">
                        {t('game_selection.game_name_label')}
                      </span>
                      <input
                        id="game-selection-create-name"
                        type="text"
                        placeholder={t('game_selection.game_name_placeholder')}
                        value={createName}
                        onChange={(e) => setCreateName(e.target.value)}
                        className="game-selection__input"
                      />
                    </label>

                    <label
                      className="game-selection__field"
                      htmlFor="game-selection-create-description"
                    >
                      <span className="game-selection__field-label">
                        {t('game_selection.game_description_label')}
                      </span>
                      <input
                        id="game-selection-create-description"
                        type="text"
                        placeholder={t('game_selection.game_description_placeholder')}
                        value={createDescription}
                        onChange={(e) => setCreateDescription(e.target.value)}
                        className="game-selection__input"
                      />
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="action-button action-button--primary game-selection__action"
                    disabled={creating}
                  >
                    <span className="action-button__text">
                      {creating ? t('game_selection.creating') : t('game_selection.create_cta')}
                    </span>
                  </button>
                </form>
              </div>
            </section>
          </div>

          <div className="game-selection__right-column">
            <section
              className="game-selection__card game-selection__card--overview"
              aria-hidden="true"
            >
              <div className="game-selection__overview-header">
                <h2 className="game-selection__overview-title">
                  {t('game_selection.overview_title')}
                </h2>
              </div>

              <div className="game-selection__overview-stats">
                {OVERVIEW_STATS.map(({ key, value, icon }) => (
                  <div key={key} className="game-selection__stat-card">
                    <div className="game-selection__stat-icon">
                      <img src={icon} alt="" />
                    </div>
                    <div className="game-selection__stat-copy">
                      <span className="game-selection__stat-value">{value}</span>
                      <span className="game-selection__stat-label">
                        {t(`game_selection.overview.${key}`)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="game-selection__card game-selection__card--join">
              <div className="game-selection__card-content game-selection__card-content--join">
                <h2 className="game-selection__card-title">
                  {t('game_selection.join_game_title')}
                </h2>

                <form onSubmit={handleJoinGame} className="game-selection__form">
                  <label className="game-selection__field" htmlFor="game-selection-join-code">
                    <span className="game-selection__field-label">
                      {t('game_selection.join_code_label')}
                    </span>
                    <input
                      id="game-selection-join-code"
                      type="text"
                      placeholder={t('game_selection.join_code_placeholder')}
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value)}
                      className="game-selection__input"
                    />
                  </label>

                  <button
                    type="submit"
                    className="action-button action-button--secondary game-selection__action"
                  >
                    <span className="action-button__text">{t('game_selection.join')}</span>
                  </button>
                </form>
              </div>
            </section>
          </div>
        </div>

        <div className="game-selection__footer">
          <p className="game-selection__footer-copy">
            {t('game_selection.footer', { year: new Date().getFullYear() })}
          </p>
          <button type="button" className="game-selection__sign-out" onClick={handleLogout}>
            {t('game_selection.sign_out')}
          </button>
        </div>
      </div>
    </div>
  )
}
